import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

/**
 * The preview trigger that sits on a collection tile.
 *
 * Fetches the collection's `preview` alternate template — the panel body only — and
 * hands it to the shared panel. The panel opens before the fetch resolves so the
 * shopper gets feedback straight away, and the fragment is cached per tile.
 *
 * @extends {Component}
 */
class CollectionPreviewComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {string | null} */
  #cachedHtml = null;

  /** The collection page this tile points at. */
  get collectionUrl() {
    return this.dataset.collectionUrl || null;
  }

  /** The fragment that fills the panel: newest first, chrome stripped off. */
  get previewUrl() {
    const url = this.collectionUrl;

    if (!url) return null;

    const previewUrl = new URL(url, window.location.origin);

    previewUrl.searchParams.set('view', 'preview');
    previewUrl.searchParams.set('sort_by', 'created-descending');

    return previewUrl.href;
  }

  /**
   * The panel shared by every tile on the page. Found by tag rather than id: the
   * panel is emitted by the section that turned previews on, so a page with two such
   * sections would otherwise carry a duplicate id.
   */
  get panel() {
    const panel = document.querySelector('collection-preview-panel');

    return panel instanceof CollectionPreviewPanel ? panel : null;
  }

  /** Warms the cache before the click lands. */
  prefetch = () => {
    if (this.#cachedHtml) return;

    this.#fetchPreview();
  };

  /**
   * Opens the panel for this tile's collection.
   * @param {Event} event - The click event.
   */
  open = async (event) => {
    // The tile is wrapped in its own link; this button must not follow it.
    event.preventDefault();
    event.stopPropagation();

    const { panel, collectionUrl } = this;

    if (!panel || !collectionUrl) return;

    panel.prepare(collectionUrl, this.dataset.collectionTitle ?? '');

    if (!this.#cachedHtml) await this.#fetchPreview();

    if (this.#cachedHtml == null) {
      panel.fail(collectionUrl);
      return;
    }

    panel.fill(this.#cachedHtml, collectionUrl);
  };

  /**
   * Fetches and caches this tile's preview fragment.
   * @returns {Promise<void>}
   */
  async #fetchPreview() {
    const url = this.previewUrl;

    if (!url) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });

      if (!response.ok) throw new Error(`Failed to fetch collection preview: HTTP error ${response.status}`);

      this.#cachedHtml = await response.text();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error(error);
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('collection-preview-component')) {
  customElements.define('collection-preview-component', CollectionPreviewComponent);
}

/**
 * The shared collection preview panel.
 *
 * Refs: `dialog` (the dialog element), `panelContent` (where the fragment goes),
 * `detailsLink` (through to the collection page) and the optional `panelTitle`.
 */
class CollectionPreviewPanel extends DialogComponent {
  requiredRefs = ['dialog', 'panelContent', 'detailsLink'];

  /**
   * The collection the panel is currently showing. Fetches that resolve after the
   * shopper has moved on to another tile are discarded rather than shown.
   * @type {string | null}
   */
  #currentUrl = null;

  /**
   * Opens the panel empty, pointed at a collection.
   * @param {string} url - The collection page URL.
   * @param {string} title - The collection title, for the panel header.
   */
  prepare(url, title) {
    const { panelContent, detailsLink, panelTitle } = this.refs;

    this.#currentUrl = url;
    detailsLink.href = url;

    if (panelTitle) {
      panelTitle.textContent = title;
      if (panelTitle instanceof HTMLAnchorElement) panelTitle.href = url;
    }

    // Drop the previous collection first, so nothing stale is ever on screen.
    panelContent.replaceChildren();
    this.dataset.state = 'loading';

    this.showDialog();
  }

  /**
   * Fills the panel with a fetched preview fragment.
   * @param {string} html - The fragment's markup.
   * @param {string} url - The collection it was fetched for.
   */
  fill(html, url) {
    if (url !== this.#currentUrl) return;

    // The fragment is this theme's own template output, not third party markup.
    this.refs.panelContent.innerHTML = html;
    this.dataset.state = 'ready';
  }

  /**
   * Leaves the shopper the header and the link through to the collection when the
   * fragment cannot be fetched.
   * @param {string} url - The collection that could not be shown.
   */
  fail(url) {
    if (url !== this.#currentUrl) return;

    this.dataset.state = 'error';
  }
}

if (!customElements.get('collection-preview-panel')) {
  customElements.define('collection-preview-panel', CollectionPreviewPanel);
}
